import React, { useState, useMemo } from "react";

import styles from "../../../styles/pages/profile/ProfilePage.module.css";
import {
  FaCommentDots,
  FaTrash,
  FaExclamationTriangle,
  FaClock,
  FaCheckCircle,
  FaThumbsUp,
  FaFilter,
  FaSort,
} from "react-icons/fa";
import { formatDate, formatRelativeTime } from "../../../utils/dateFormatter";
import api from "../../../utils/api/axiosInstance";
import AnswersViewSkeleton from "./AnswersViewSkeleton";
import ConfirmModal from "../../common/ConfirmModal";
import useBulkSelection from "../bulk/useBulkSelection";
import BulkSelectBar, {
  SelectCheckbox,
  SelectModeButton,
} from "../bulk/BulkSelectBar";
import bulkStyles from "../bulk/BulkSelectBar.module.css";
import runBulkRequests from "../../../utils/runBulkRequests";

const AnswersView = ({
  answers,
  loading,
  error,
  onRefresh,
  showToast,
  currentTab,
}) => {
  const [filterType, setFilterType] = useState("all"); // 'all' | 'accepted' | 'mostVoted'
  const [sortBy, setSortBy] = useState("date"); // 'date' | 'votes'
  const [deleting, setDeleting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [pendingIds, setPendingIds] = useState([]);

  // Filter and sort answers
  const filteredAndSortedAnswers = useMemo(() => {
    let result = [...answers];

    // Filter by type
    if (filterType === "accepted") {
      result = result.filter(
        (answer) => answer.isBestAnswer || answer.isAccepted,
      );
    } else if (filterType === "mostVoted") {
      result = result.filter(
        (answer) => (answer.helpful || answer.upvotes || 0) >= 5,
      );
    }

    // Sort
    result.sort((a, b) => {
      if (sortBy === "date") {
        return new Date(b.createdAt) - new Date(a.createdAt);
      } else if (sortBy === "votes") {
        const votesA = a.helpful || a.upvotes || 0;
        const votesB = b.helpful || b.upvotes || 0;
        return votesB - votesA;
      }
      return 0;
    });

    return result;
  }, [answers, filterType, sortBy]);

  const visibleIds = useMemo(
    () => filteredAndSortedAnswers.map((answer) => answer._id).filter(Boolean),
    [filteredAndSortedAnswers],
  );
  const {
    selectMode,
    selectedIds,
    allVisibleSelected,
    toggle,
    selectAllVisible,
    exitSelectMode,
    toggleSelectMode,
    setSelectedIds,
  } = useBulkSelection(visibleIds);

  const openDelete = (ids) => {
    if (!ids.length || deleting) return;
    setPendingIds(ids);
    setShowDeleteModal(true);
  };

  const confirmDeleteAnswer = async () => {
    if (pendingIds.length === 0) return;
    const ids = pendingIds;

    try {
      setDeleting(true);
      setShowDeleteModal(false);
      const { succeeded, failed, failedIds } = await runBulkRequests(ids, (id) => {
        const answer = answers.find((item) => item._id === id);
        const questionId = answer?.question?._id || answer?.questionId;
        if (!questionId) throw new Error("Question ID not found");
        return api.delete(`/questions/${questionId}/answers/${id}`);
      });
      if (succeeded > 0) {
        showToast({
          message:
            succeeded === 1
              ? "Answer deleted successfully"
              : `${succeeded} answers deleted`,
          type: "success",
        });
        onRefresh();
      }
      if (failed > 0) {
        showToast({
          message:
            succeeded === 0
              ? "Failed to delete answer"
              : `${failed} answers could not be deleted`,
          type: "error",
        });
        setSelectedIds(failedIds);
      } else if (selectMode) {
        exitSelectMode();
      }
    } catch (err) {
      console.error("Error deleting answer:", err);
      showToast({
        message: err.response?.data?.message || "Failed to delete answer",
        type: "error",
      });
    } finally {
      setDeleting(false);
      setPendingIds([]);
    }
  };

  if (loading) {
    return <AnswersViewSkeleton />;
  }

  if (error) {
    return (
      <div className={styles.contentSection}>
        <div className={styles.errorState}>
          <div className={styles.errorStateIcon}>
            <FaExclamationTriangle size={48} />
          </div>
          <h3>Unable to Load Answers</h3>
          <p>{error}</p>
          <button className="buttonPrimary" onClick={onRefresh}>
            <FaClock /> Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.contentSection}>
      <div className={styles.sectionHeader}>
        <div>
          <h2 className={styles.sectionTitle}>
            <FaCommentDots className={styles.sectionIcon} />
            My Answers
          </h2>
          {answers.length > 0 && (
            <p className={styles.sectionSubtitle}>
              {filteredAndSortedAnswers.length} answer
              {filteredAndSortedAnswers.length === 1 ? "" : "s"}
              {filterType === "accepted" && " · Accepted"}
              {filterType === "mostVoted" && " · 5+ votes"}
            </p>
          )}
        </div>
        {answers.length > 0 && (
          <div className={styles.bulkActions}>
            <button
              className="buttonSecondary"
              onClick={onRefresh}
              disabled={loading}
              title="Refresh Answers"
            >
              <FaClock /> Refresh
            </button>
            <SelectModeButton
              active={selectMode}
              onClick={toggleSelectMode}
              disabled={deleting}
            />
          </div>
        )}
      </div>

      {selectMode && (
        <BulkSelectBar
          selectedCount={selectedIds.length}
          allSelected={allVisibleSelected}
          hasHidden={answers.length > filteredAndSortedAnswers.length}
          busy={deleting}
          onSelectAll={selectAllVisible}
          onDelete={() => openDelete(selectedIds)}
          noun="answers"
        />
      )}

      {answers.length === 0 ? (
        <div className={styles.emptyState}>
          <div className={styles.emptyStateIcon}>
            <FaCommentDots size={64} />
          </div>
          <h3>No Answers Yet</h3>
          <p>
            You haven't answered any questions yet. Help the community by
            sharing your knowledge and expertise.
          </p>
        </div>
      ) : (
        <>
          {/* Filters and Sort */}
          <div className={styles.filterBar}>
            <div className={styles.filterGroup}>
              <FaFilter className={styles.filterIcon} />
              <label>Filter:</label>
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className={styles.filterSelect}
              >
                <option value="all">All Answers</option>
                <option value="accepted">Accepted Answers</option>
                <option value="mostVoted">Most Voted (5+)</option>
              </select>
            </div>

            <div className={styles.filterGroup}>
              <FaSort className={styles.filterIcon} />
              <label>Sort by:</label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className={styles.filterSelect}
              >
                <option value="date">Most Recent</option>
                <option value="votes">Most Votes</option>
              </select>
            </div>
          </div>

          <div className={styles.answersGrid}>
            {filteredAndSortedAnswers.map((answer) => {
              const isSelected = selectedIds.includes(answer._id);
              return (
              <div
                key={answer._id}
                className={`${styles.answerCard} ${
                  selectMode ? bulkStyles.selectable : ""
                } ${isSelected ? bulkStyles.cardSelected : ""}`}
                onClick={(event) => {
                  if (!selectMode) return;
                  if (event.target.closest("a, button, input, label")) return;
                  toggle(answer._id);
                }}
              >
                <div className={styles.answerCardHeader}>
                  <div className={styles.answerQuestionInfo}>
                    <div className={styles.answerQuestionDetails}>
                      <span className={styles.answerQuestionTitle}>
                        {answer.question?.title || "Question"}
                      </span>
                      <div className={styles.answerHeaderInfo}>
                        {(answer.isBestAnswer || answer.isAccepted) && (
                          <span
                            className={styles.acceptedBadge}
                            title="Accepted Answer"
                          >
                            <FaCheckCircle /> Accepted
                          </span>
                        )}
                        <span className={styles.answerStat}>
                          <FaThumbsUp /> {answer.helpful || answer.upvotes || 0}{" "}
                          votes
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className={styles.answerCardMeta}>
                    <span className={styles.answerDate}>
                      {formatDate(answer.createdAt)}
                    </span>
                  </div>

                  <div className={styles.answerActions}>
                    {selectMode ? (
                      <SelectCheckbox
                        checked={isSelected}
                        onChange={() => toggle(answer._id)}
                        label={`Select answer to ${answer.question?.title || "question"}`}
                        disabled={deleting}
                      />
                    ) : (
                      <button
                        onClick={() => openDelete([answer._id])}
                        className={`${styles.answerActionButton} ${styles.answerDeleteButton}`}
                        disabled={deleting}
                        title="Delete Answer"
                      >
                        <FaTrash size={16} />
                      </button>
                    )}
                  </div>
                </div>

                {(answer.answer || answer.content || "").trim() && (
                  <div className={styles.activityCommentText}>
                    {(answer.answer || answer.content).trim()}
                  </div>
                )}
              </div>
              );
            })}
          </div>
        </>
      )}

      {showDeleteModal && (
        <ConfirmModal
          message={
            pendingIds.length > 1
              ? `Delete ${pendingIds.length} answers?`
              : "Are you sure you want to delete this answer?"
          }
          onConfirm={confirmDeleteAnswer}
          onClose={() => {
            if (deleting) return;
            setShowDeleteModal(false);
            setPendingIds([]);
          }}
        />
      )}
    </div>
  );
};

export default React.memo(AnswersView);
